import { useBraze } from '../../braze/useBraze';
import { BRAZE_INAPP_MESSAGE } from '../routeNames';
import { useNavigateWithHistory } from '../useNavigateWithHistory';
import { useAuthContext } from '../../contexts/AuthContext/AuthContext';
import { useAppFeaturesContext } from '../../contexts/AppFeaturesContext/AppFeaturesContext';
import { LoggedInUser } from '../../../common/types';
import { ReactNode, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type RedirectProps = {
  isBrazeInitialized: boolean;
  inAppMessagesCount: number;
  e2eTests: boolean;
  user: LoggedInUser | null | undefined;
};

export const shouldRedirectToBraze = ({
  isBrazeInitialized,
  inAppMessagesCount,
  e2eTests,
  user,
}: RedirectProps) => {
  // must have braze
  if (!isBrazeInitialized) return false;

  // must have a user
  if (!user) return false;

  // braze shouldn't initialize for subaccounts but if it does double make
  // sure we don't redirect for children's profiles
  if (user.isSubAccount) return false;

  // don't mess up e2e tests
  if (e2eTests) return false;

  // only redirect if there are messages in the queue
  return inAppMessagesCount > 0;
};

const BrazeRedirect: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuthContext();
  const { inAppMessages, isBrazeInitialized } = useBraze();
  const { e2eTests } = useAppFeaturesContext();
  const navigate = useNavigateWithHistory();
  const [searchParams] = useSearchParams();

  const shouldRedirect = useMemo(
    () =>
      shouldRedirectToBraze({
        isBrazeInitialized,
        e2eTests,
        inAppMessagesCount: inAppMessages.length,
        user,
      }),
    [e2eTests, inAppMessages.length, isBrazeInitialized, user]
  );

  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/${BRAZE_INAPP_MESSAGE}?${searchParams.toString()}`);
    }
  }, [navigate, searchParams, shouldRedirect]);

  return !shouldRedirect ? children : null;
};

export default BrazeRedirect;
